import React, { useState } from "react";

import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import dayjs from "dayjs";
import "dayjs/locale/en";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import apiService from "../services/api";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("en");

const AutopilotSettings = ({
  lead,
  handleChangeLeadData,
  handleCloseResponseMenu,
}) => {
  const [selectedDateTime, setSelectedDateTime] = useState(
    lead?.parsedTime ? dayjs(lead.parsedTime as any) : null
  );
  if (lead?.answerType !== "Autopilot") return null;
  
  // Normalize boolean to avoid undefined showing both toggles ON
  const isInstant = lead?.instantly !== false;

  const handleChangeScheduleDelivery = async (e) => {
    const scheduleOn = e.target.checked;
    const instantly = !scheduleOn;
    // Mutual exclusivity
    handleChangeLeadData(lead.id, "instantly", instantly);
    
    try {
      await apiService.configureAutopilot(lead.id, { 
        answerType: "Autopilot",
        instantly,
        autopilotTime: instantly ? undefined : lead?.autopilotTime,
        parsedTime: instantly ? undefined : lead?.parsedTime?.toISOString()
      });
    } catch (error) {
      console.error('Error updating autopilot settings:', error);
    }
    
    if (scheduleOn) {
      handleChangeLeadData(lead.id, "autopilotTime", 0);
    } else {
      // If schedule turned OFF, auto-enable Instant
      handleChangeLeadData(lead.id, "instantly", true);
      handleChangeLeadData(lead.id, "autopilotTime", undefined);
      handleChangeLeadData(lead.id, "parsedTime", undefined);
    }
  };

  const handleToggleInstant = async (e) => {
    const turnOnInstant = e.target.checked;
    // Mutual exclusivity: turning Instant ON disables Scheduled
    handleChangeLeadData(lead.id, "instantly", turnOnInstant);
    if (turnOnInstant) {
      handleChangeLeadData(lead.id, "autopilotTime", undefined);
      handleChangeLeadData(lead.id, "parsedTime", undefined);
    }
    try {
      await apiService.configureAutopilot(lead.id, {
        answerType: "Autopilot",
        instantly: turnOnInstant,
        autopilotTime: turnOnInstant ? undefined : lead?.autopilotTime,
        parsedTime: turnOnInstant ? undefined : lead?.parsedTime?.toISOString(),
      });
    } catch (error) {
      console.error('Error toggling instant mode:', error);
    }
  };

  const handleChangeDateTime = async (newValue) => {
    const asDayjs = newValue ? dayjs(newValue) : null;
    setSelectedDateTime(asDayjs as any);
    const formattedTime = asDayjs ? String(formatDateTime(asDayjs.toDate())) : '';
    
    handleChangeLeadData(lead.id, "autopilotTime", formattedTime);
    handleChangeLeadData(lead.id, "parsedTime", asDayjs ? asDayjs.toDate() : undefined);
    
    try {
      await apiService.configureAutopilot(lead.id, { 
        answerType: "Autopilot",
        instantly: false,
        autopilotTime: formattedTime,
        parsedTime: asDayjs ? asDayjs.toISOString() : undefined
      });
    } catch (error) {
      console.error('Error updating autopilot time:', error);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear().toString().slice(2);

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${month}.${day}.${year} - ${hours}:${minutes} ${ampm}`;
  };

  return (
    <div className="px-4 py-3 flex flex-col gap-6">
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={isInstant}
            onChange={handleToggleInstant}
            sx={{
              width: 42,
              height: 24,
              padding: "0px",
              "& .MuiSwitch-switchBase": {
                padding: 0,
                margin: "2px",
                "&.Mui-checked": {
                  transform: "translateX(18px)",
                  color: "#fff",
                  "& + .MuiSwitch-track": {
                    backgroundColor: `${isInstant ? "#407097" : "#CBD5E0"}`,
                    opacity: 1,
                  },
                },
              },
              "& .MuiSwitch-thumb": {
                width: 20,
                height: 20,
              },
              "& .MuiSwitch-track": {
                borderRadius: 26 / 2,
                backgroundColor: `${isInstant ? "#407097" : "#CBD5E0"}`,
                opacity: 1,
              },
            }}
          />
        }
        label="Instantly"
        labelPlacement="start"
        sx={{
          marginLeft: 0,
          width: "100%",
          justifyContent: "space-between",
          ".MuiFormControlLabel-label": {
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
            fontWeight: "600",
            color: "#222830",
          },
        }}
      />
      <div className="mt-3">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!isInstant}
              onChange={(e) => handleChangeScheduleDelivery(e)}
              sx={{
                width: 42,
                height: 24,
                padding: 0,
                "& .MuiSwitch-switchBase": {
                  padding: 0,
                  margin: "2px",
                  "&.Mui-checked": {
                    transform: "translateX(18px)",
                    color: "#fff",
                    "& + .MuiSwitch-track": {
                    backgroundColor: `${!isInstant ? "#407097" : "#CBD5E0"}`,
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 20,
                  height: 20,
                },
                "& .MuiSwitch-track": {
                  borderRadius: "12px",
                  backgroundColor: `${!isInstant ? "#407097" : "#CBD5E0"}`,
                  opacity: 1,
                },
              }}
            />
          }
          label="Schedule Response"
          labelPlacement="start"
          sx={{
            marginLeft: 0,
            width: "100%",
            justifyContent: "space-between",
            ".MuiFormControlLabel-label": {
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: "600",
              color: "#222830",
            },
          }}
        />
        {!isInstant && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              onChange={(newValue) => handleChangeDateTime(newValue)}
              minDateTime={dayjs()}
              value={selectedDateTime}
              slotProps={
                {
                  desktopPaper: {
                    sx: {
                      mt: 1.25,
                      transform: "translateX(calc(0% - 30px))",
                      borderRadius: "12px",
                      border: "1px solid #323C48",
                      boxShadow: "box-shadow: 0 4px 24px #CBD5E066",
                      "& .Mui-selected": {
                        backgroundColor: "#407097 !important",
                        color: "#FFFFFF !important",
                      },
                    },
                  },
                  textField: {
                    placeholder: "Pick date & time",
                    sx: {
                      width: "100%",
                      marginTop: "15px",
                      "& .MuiInputLabel-root": {
                        display: "none",
                      },
                      "& .MuiOutlinedInput-root": {
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#222830",
                        height: "40px",
                        borderRadius: "12px",
                        backgroundColor: "#F3F5F7",
                        display: "flex",
                        alignItems: "center",
                        "&.Mui-focused": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#407097",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#C3CBD5",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 12px",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#323C48",
                      },
                    },
                  },
                } as any
              }
            />
          </LocalizationProvider>
        )}
      </div>
      <div className="flex gap-3 mt-4">
        <Button
          variant="outlined"
          onClick={handleCloseResponseMenu}
          sx={{
            flex: 1,
            height: "40px",
            borderRadius: "100px",
            textTransform: "none",
            border: "1px solid #222830",
            color: "#222830",
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCloseResponseMenu}
          sx={{
            flex: 1,
            height: "40px",
            boxShadow: "none",
            borderRadius: "100px",
            textTransform: "none",
            backgroundColor: "#407097",
            color: "#FFFFFF",
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
            fontWeight: "600",
            "&:hover": {
              backgroundColor: "#ABB7C4",
              boxShadow: "none",
            },
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default AutopilotSettings;
